/**
 * When a session ENDS.
 *
 * Until this module existed, nothing ended one. A session began at init() and
 * ran until the tab closed, so a console left open over a weekend was a single
 * session whose `duration_ms` counted the weekend. On prod that produced
 * sessions of 596 hours — the int4 ceiling is 24.9 days, and we were reaching
 * for it — and it meant our "sessions" were not the same unit every other
 * replay vendor sells. LogRocket's web SDK ends a session after 30 minutes of
 * inactivity; so, now, do we.
 *
 * Two independent ends:
 *
 *   • IDLE — no user input for `idleTimeoutMs`. The session is closed and NO
 *     new one opens until the person comes back, so a parked tab stops costing
 *     anything instead of accruing an empty timeline. This is the one that
 *     matters: it is both the cost fix and the reason a session count of ours
 *     can be compared with a competitor's.
 *
 *   • MAX LENGTH — `maxSessionMs` of genuinely continuous use. A backstop, not
 *     a policy: something recording for a solid day is a kiosk, a stuck
 *     `setInterval`, or a bug, and an unbounded `t` eventually overflows the
 *     int4 columns downstream. Mobile SDKs across the market cap at 24h and so
 *     do we.
 *
 * USER activity, not APP activity. The tracker already tracks the latter —
 * `InstrumentCtx.markActivity`, fed by DOM mutations, navigations and network
 * responses, which dead-click detection needs. It is the wrong signal here and
 * would defeat the whole feature: a B2B app that polls an endpoint every 10s
 * "is active" forever with nobody at the keyboard, which is precisely the
 * session we are trying to end. So this module listens for human input and
 * nothing else.
 */
import type { Teardown } from './context.js';

/**
 * Deliberate human input. Each of these requires a hand: there is no way for an
 * idle page to produce one by itself.
 *
 * Notably ABSENT, and each for a reason found the hard way:
 *
 *   • `scroll` — a trusted `scroll` event fires when SCRIPT sets `scrollTop`, and
 *     isTrusted cannot tell the two apart (the browser, not the script, dispatches
 *     it). Any auto-scrolling log, chat or activity pane — which is most of the
 *     B2B consoles this matters for — would hold its session open forever. The
 *     human ways to scroll are all still covered: `wheel` for a trackpad or
 *     mouse, `pointerdown` for a scrollbar drag, `keydown` for Page Down, and
 *     `touchmove` for a swipe.
 *   • `mousemove` — fires when an animation moves the page under a stationary
 *     cursor, same failure in a different costume.
 *   • `visibilitychange` — returning to a tab is not work. It does trigger an
 *     immediate re-check (see the client), because a tab frozen in the background
 *     may be overdue for an end, but it never counts as activity.
 *
 * Registered in the CAPTURE phase so events that don't bubble, or that a page
 * stops mid-flight, are still seen.
 */
const USER_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'touchmove',
  'wheel',
  'input',
] as const;

const LISTENER_OPTS: AddEventListenerOptions = { capture: true, passive: true };

/** Call `onActivity` on any genuine user input. Installed once and kept alive
 *  across the dormant gap — it is what wakes a session back up, so it must
 *  outlive the instrumenters that teardown() removes. */
export function watchUserActivity(onActivity: () => void): Teardown {
  if (typeof window === 'undefined') return () => {};
  const handler = () => onActivity();
  for (const type of USER_EVENTS) window.addEventListener(type, handler, LISTENER_OPTS);
  return () => {
    for (const type of USER_EVENTS) window.removeEventListener(type, handler, LISTENER_OPTS);
  };
}

export type SessionVerdict = 'active' | 'idle' | 'expired';

export interface LifetimeInput {
  /** Wall clock now (epoch ms). */
  now: number;
  /** Epoch ms of the last user input. */
  lastUserActivityAt: number;
  /** Age of the session in ms — `clock.now()`, which for a continued session
   *  counts from the ORIGINAL start, not this page's. */
  sessionT: number;
  /** 0 disables the idle end. */
  idleTimeoutMs: number;
  /** 0 disables the max-length end. */
  maxSessionMs: number;
}

/**
 * Pure: should this session end, and why?
 *
 * Idle is checked FIRST and deliberately. A session that is both over the
 * length cap and idle must end as 'idle', because the two outcomes differ:
 * 'expired' rotates straight into a fresh recording session (the user is mid-
 * flow), whereas 'idle' leaves the client dormant. Reversing the order would
 * open a new session for someone who walked away hours ago.
 */
export function sessionVerdict(i: LifetimeInput): SessionVerdict {
  if (i.idleTimeoutMs > 0 && i.now - i.lastUserActivityAt >= i.idleTimeoutMs) return 'idle';
  if (i.maxSessionMs > 0 && i.sessionT >= i.maxSessionMs) return 'expired';
  return 'active';
}

/**
 * How often to test the verdict.
 *
 * Bounded to [5s, 30s]: the check is a subtraction and two comparisons, so the
 * cost is noise, and the upper bound is what keeps the max-length end from
 * overshooting. Background tabs are throttled to ~1/min by every browser and
 * frozen tabs don't tick at all — that is fine and is why the verdict compares
 * `Date.now()` rather than counting ticks. A tab frozen for six hours returns a
 * correct 'idle' on its first tick after waking.
 */
export function watchIntervalMs(idleTimeoutMs: number): number {
  const target = Math.floor((idleTimeoutMs > 0 ? idleTimeoutMs : 60_000) / 20);
  return Math.max(5_000, Math.min(30_000, target));
}
